/**
 * Experiment B — Impact compression.
 *
 * Store: when the player hits a compression surface, part of the normal speed
 * the slide response removed is kept as a decaying "charge" (the outgoing
 * velocity itself is unchanged, so storing is energy-neutral).
 * Release: a jump pressed within the release window spends the charge as an
 * impulse along the impact normal, tilted by the player's input along the
 * surface. Energy bound: |release| ≤ storeFraction × efficiency × impactSpeed
 * ≤ impactSpeed, so a rebound never exceeds the speed that was lost.
 * Charge does not stack: a new impact replaces it if larger, or if the old
 * charge is older than holdTicks (an unreleased charge never blocks later impacts).
 */
import { Vec3, clamp } from '../../core/Vec3';
import type { RuleState } from '../PlayerState';
import type { ImpactRule, JumpRule } from '../MovementRules';
import { surfaceStrength } from './surfaceFilter';
import type { ProximityContact } from '../../simulation/CollisionQueries';

export class CompressionState implements RuleState {
  charge = 0;
  readonly normal = new Vec3();
  impactTick = -1;

  reset(): void {
    this.charge = 0;
    this.normal.set(0, 0, 0);
    this.impactTick = -1;
  }

  snapshot(): readonly number[] {
    return [this.charge, this.normal.x, this.normal.y, this.normal.z, this.impactTick];
  }
}

export interface CompressionStoreParams extends Record<string, number> {
  storeFraction: number;
  /** Ticks a stored charge blocks smaller impacts; keep equal to the release window. */
  holdTicks: number;
  minImpactSpeed: number;
  allSurfaces: number;
}

export interface CompressionReleaseParams extends Record<string, number> {
  windowTicks: number;
  decayRate: number;
  efficiency: number;
  steer: number;
}

const STATE_KEY = 'exp.compression';
const probe = { material: null } as unknown as ProximityContact;
const dir = new Vec3();
const along = new Vec3();

export function createCompressionRules(): [ImpactRule<CompressionStoreParams>, JumpRule<CompressionReleaseParams>] {
  const store: ImpactRule<CompressionStoreParams> = {
    id: 'exp.compression.store',
    description: 'B: stores part of the impact normal speed on compression surfaces.',
    stage: 'impact',
    order: 10,
    conflictsWith: [],
    stateKey: STATE_KEY,
    energy: 'dissipate',
    params: { storeFraction: 0.7, holdTicks: 24, minImpactSpeed: 4, allSurfaces: 0 },
    paramSpecs: {
      storeFraction: { min: 0, max: 1, description: 'Fraction of impact normal speed stored' },
      holdTicks: { min: 1, max: 240, description: 'Ticks an unreleased charge is kept before any new impact replaces it' },
      minImpactSpeed: { min: 0, max: 50, description: 'Impacts slower than this store nothing (m/s)' },
      allSurfaces: { min: 0, max: 1, description: 'Strength on non-compression surfaces' },
    },
    proximityRange: () => 0,
    createState: () => new CompressionState(),
    applies: (ctx) => ctx.state.alive && ctx.ruleState !== null,
    onImpact(ctx, p, impact) {
      if (impact.impactSpeed < p.minImpactSpeed) return;
      (probe as { material: unknown }).material = impact.material;
      const strength = clamp(surfaceStrength(probe, 'compression', p.allSurfaces), 0, 1);
      if (strength <= 0) return;
      const s = ctx.ruleState as CompressionState;
      const charge = impact.impactSpeed * p.storeFraction * strength;
      const expired = s.impactTick < 0 || ctx.state.tick - s.impactTick > p.holdTicks;
      if (expired || charge > s.charge) {
        s.charge = charge;
        s.normal.copy(impact.normal);
        s.impactTick = ctx.state.tick;
      }
    },
  };

  const release: JumpRule<CompressionReleaseParams> = {
    id: 'exp.compression.release',
    description: 'B: a jump within the window releases stored charge along the impact normal.',
    stage: 'jump',
    order: 10,
    conflictsWith: [],
    requires: ['exp.compression.store'],
    stateKey: STATE_KEY,
    energy: 'inject',
    params: { windowTicks: 24, decayRate: 3, efficiency: 0.9, steer: 0.6 },
    paramSpecs: {
      windowTicks: { min: 1, max: 240, description: 'Ticks after impact in which a jump releases' },
      decayRate: { min: 0, max: 30, description: 'Exponential charge decay (1/s)' },
      efficiency: { min: 0, max: 1, description: 'Fraction of the charge returned on release' },
      steer: { min: 0, max: 2, description: 'How far input along the surface tilts the release' },
    },
    proximityRange: () => 0,
    applies: (ctx, p) => {
      const s = ctx.ruleState as CompressionState | null;
      return !!s && s.charge > 0 && s.impactTick >= 0 && ctx.state.tick - s.impactTick <= p.windowTicks && ctx.state.alive;
    },
    contribute(ctx, p, _ground, out) {
      const s = ctx.ruleState as CompressionState;
      const ageSeconds = (ctx.state.tick - s.impactTick) * ctx.dt;
      const magnitude = s.charge * Math.exp(-p.decayRate * ageSeconds) * p.efficiency;
      along.copy(ctx.intent.wishDir).projectOnPlane(s.normal).scale(ctx.intent.wishStrength * p.steer);
      dir.copy(s.normal).add(along).normalize();
      out.addScaled(dir, magnitude);
      s.reset();
      return true;
    },
  };
  return [store, release];
}
